import React, { useEffect, useState, useRef } from 'react';
import type { SearchQuery } from '../../types';

interface AlternativeDate {
    date: string;    // "2026-06-03"
    price: number;
    currency: string;
}

interface AlternativeDatesBarProps {
    searchQuery: SearchQuery | null;
    hotelId?: string;
    language: 'en' | 'sv';
    onDateSelect: (newCheckIn: string, nights: number) => void;
    isUpdating?: boolean;
}

const AlternativeDatesBar: React.FC<AlternativeDatesBarProps> = ({
    searchQuery,
    hotelId,
    language,
    onDateSelect,
    isUpdating = false,
}) => {
    const [dates, setDates] = useState<AlternativeDate[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    // Current check-in from search query
    const currentCheckIn = searchQuery?.checkIn || '';

    useEffect(() => {
        if (!searchQuery?.destinationId || !currentCheckIn) {
            setLoading(false);
            return;
        }

        const fetchDates = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    arrivalId: searchQuery.destinationId,
                    checkIn: currentCheckIn,
                    nights: String(searchQuery.nights || 7),
                    adults: String(searchQuery.adults || 2),
                    children: String(searchQuery.children || 0),
                    daysAhead: '21',
                    daysBehind: '7',
                });
                // Pass destination name for cross-system cache matching
                // (Reisegiganten cache uses different IDs than Tourvisio frontend)
                if (searchQuery.destinationName) {
                    params.set('arrivalName', searchQuery.destinationName);
                }
                if (searchQuery.departureId) {
                    params.set('departureId', searchQuery.departureId);
                }
                // Filter by specific hotel for hotel-detail-page context
                if (hotelId) {
                    params.set('hotelId', hotelId);
                }
                const res = await fetch(`/api/tour-cache/alternative-dates?${params.toString()}`);
                const data = await res.json();
                if (data.success && data.dates?.length > 0) {
                    setDates(data.dates);
                }
            } catch (err) {
                console.warn('[AlternativeDatesBar] Fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchDates();
    }, [searchQuery?.departureId, searchQuery?.destinationId, searchQuery?.destinationName, currentCheckIn, searchQuery?.nights, searchQuery?.adults, searchQuery?.children]);

    // Scroll to current date on mount
    useEffect(() => {
        if (dates.length > 0 && scrollRef.current) {
            const currentIdx = dates.findIndex(d => d.date === currentCheckIn);
            if (currentIdx >= 0) {
                const el = scrollRef.current.children[currentIdx] as HTMLElement;
                if (el) {
                    el.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
                }
            }
        }
    }, [dates, currentCheckIn]);

    const handleDateClick = (date: string) => {
        if (date === currentCheckIn || isUpdating) return;
        setSelectedDate(date);
        onDateSelect(date, searchQuery?.nights || 7);
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
        }
    };

    // Don't render if no data or loading with no results
    if (!loading && dates.length === 0) return null;

    // Find cheapest date
    const cheapestPrice = dates.length > 0 ? Math.min(...dates.map(d => d.price)) : 0;

    // Format date for display
    const formatDateLabel = (dateStr: string): { day: string; date: string; month: string } => {
        const d = new Date(dateStr + 'T00:00:00');
        const dayNames = language === 'sv'
            ? ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör']
            : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const monthNames = language === 'sv'
            ? ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
            : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return {
            day: dayNames[d.getDay()],
            date: String(d.getDate()),
            month: monthNames[d.getMonth()],
        };
    };

    const formatPrice = (price: number): string => {
        if (price >= 10000) {
            return Math.round(price / 1000) + 'k';
        }
        return price.toLocaleString('sv-SE');
    };

    return (
        <div className="relative w-full bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-sm z-10">
            {/* Loading skeleton */}
            {loading && (
                <div className="flex items-center gap-2 px-12 py-3 overflow-hidden">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="flex-shrink-0 w-20 h-12 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
                    ))}
                </div>
            )}

            {/* Dates bar */}
            {!loading && dates.length > 0 && (
                <>
                    {/* Left scroll arrow */}
                    <button
                        onClick={() => scroll('left')}
                        className="absolute left-0 top-0 bottom-0 w-10 flex items-center justify-center bg-gradient-to-r from-white via-white/90 to-transparent dark:from-gray-900 dark:via-gray-900/90 z-10 hover:from-gray-50 transition-colors"
                        aria-label="Scroll left"
                    >
                        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Scrollable dates container */}
                    <div
                        ref={scrollRef}
                        className="flex items-stretch gap-1 px-12 py-2 overflow-x-auto scrollbar-hide"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {dates.map((item) => {
                            const isCurrent = item.date === currentCheckIn;
                            const isSelected = item.date === selectedDate;
                            const isCheapest = item.price === cheapestPrice;
                            const isActive = isCurrent || isSelected;
                            const label = formatDateLabel(item.date);

                            return (
                                <button
                                    key={item.date}
                                    onClick={() => handleDateClick(item.date)}
                                    disabled={isUpdating && !isCurrent}
                                    className={`
                                        flex-shrink-0 flex flex-col items-center justify-center px-3 py-1.5 rounded-lg
                                        min-w-[72px] transition-all duration-200 relative group
                                        ${isActive
                                            ? 'bg-teal-600 text-white shadow-md shadow-teal-600/25 scale-105'
                                            : isCheapest
                                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800'
                                                : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-100 dark:border-gray-700'
                                        }
                                        ${isUpdating && !isCurrent ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
                                    `}
                                >
                                    {/* Cheapest badge */}
                                    {isCheapest && !isActive && (
                                        <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap leading-none">
                                            {language === 'sv' ? 'Billigast' : language === 'da' ? 'Billigst' : 'Cheapest'}
                                        </span>
                                    )}

                                    {/* Day name */}
                                    <span className={`text-[10px] font-medium uppercase tracking-wider ${isActive ? 'text-white/80' : 'text-gray-400 dark:text-gray-500'}`}>
                                        {label.day}
                                    </span>

                                    {/* Date */}
                                    <span className={`text-sm font-bold leading-none ${isActive ? 'text-white' : ''}`}>
                                        {label.date} {label.month}
                                    </span>

                                    {/* Price */}
                                    <span className={`text-[11px] font-semibold leading-none mt-0.5 ${
                                        isActive
                                            ? 'text-white/90'
                                            : isCheapest
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-gray-500 dark:text-gray-400'
                                    }`}>
                                        {formatPrice(item.price)} {item.currency}
                                    </span>

                                    {/* Current indicator */}
                                    {isCurrent && (
                                        <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full shadow-sm" />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Right scroll arrow */}
                    <button
                        onClick={() => scroll('right')}
                        className="absolute right-0 top-0 bottom-0 w-10 flex items-center justify-center bg-gradient-to-l from-white via-white/90 to-transparent dark:from-gray-900 dark:via-gray-900/90 z-10 hover:from-gray-50 transition-colors"
                        aria-label="Scroll right"
                    >
                        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    {/* Updating overlay */}
                    {isUpdating && (
                        <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 flex items-center justify-center backdrop-blur-[1px] z-20">
                            <div className="flex items-center gap-2 text-teal-600 dark:text-teal-400 text-sm font-medium">
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                {language === 'sv' ? 'Uppdaterar priser...' : language === 'da' ? 'Opdaterer priser...' : 'Updating prices...'}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AlternativeDatesBar;
